import { useQueryClient } from '@tanstack/react-query';
import { useDependency } from 'ditox-react';
import { produce } from 'immer';
import { useSubscription } from 'observable-hooks';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import useSound from 'use-sound';
import MessageSound from '@/assets/sounds/ding.mp3?url';
import type { ApplicationDataType } from '@/components/contacts/application-list/application-list-item';
import { WEB_SOCKET_MANAGER_TOKEN } from '@/container';
import { useCommDispatcher } from '@/hooks/comm/use-comm';
import { friendsQueryOptions } from '@/hooks/contacts/use-friends';
import { groupsQueryOptions } from '@/hooks/contacts/use-groups';
import { generateGroupInfoQueryOptions } from '@/hooks/groups/use-group-info';
import { generateGroupJoinApplicationQueryOptions } from '@/hooks/groups/use-group-join-applications';
import { allChatSessionQueryOptions } from '@/hooks/sessions/use-conversation-sessions';
import { generateSessionSettingsQueryOptions } from '@/hooks/sessions/use-session-settings';
import useNotificationSettings from '@/hooks/settings/use-notification-settings';
import { friendApplicationListQueryOptions } from '@/hooks/users/use-friend-applications';
import { generateFriendInfoQueryOptions } from '@/hooks/users/use-friend-info';
import useSignOut from '@/hooks/users/use-sign-out';
import { conversationMessageUnionScheme } from '@/models/conversations/conversation';
import { SENTRY_LOG, simpleLogger } from '@/services/sentry';
import { useAuthStore } from '@/stores/auth';
import { useConfigurationStore } from '@/stores/configuration';
import { useConversationSessionStore } from '@/stores/conversation-session';
import { useDeviceStore } from '@/stores/device';
import { useMessageStore } from '@/stores/message';
import { usePeriodicMessageStore } from '@/stores/periodic-message';
import { getSessionByKey } from '@/utils/cache-session';
import { isGroupMemberAdjustmentMessage, transformMessage } from '@/utils/message';
import { generateSessionKey, generateSessionKeyFromMessage } from '@/utils/session';
import useUserPlatform from './use-user-platform';

export default function useWebSocketManager() {
  const webSocketManager = useDependency(WEB_SOCKET_MANAGER_TOKEN);
  const { i18n, t } = useTranslation();
  const queryClient = useQueryClient();
  const auth = useAuthStore((s) => s.auth);
  const endpoint = useConfigurationStore((s) => s.endpoint.WEBSOCKET_ENDPOINT);
  const { touchStart } = useDeviceStore();
  const { isIOS } = useUserPlatform();
  const { dispatch } = useCommDispatcher();

  const [playMessageSound] = useSound(MessageSound, {
    soundEnabled: !isIOS || touchStart,
  });
  const { data: notificationSettings } = useNotificationSettings();
  const { disposeServices } = useSignOut();

  useSubscription(webSocketManager.event$, async ({ action, params }) => {
    switch (action) {
      case 'stateChange':
        if (params.state === 'open') {
          if (params.endpoint) {
            useConfigurationStore.getState().setEndpoint({
              WEBSOCKET_ENDPOINT: params.endpoint,
            });
          }
        }

        if (params.state === 'open') {
          simpleLogger({
            name: SENTRY_LOG.WEBSOCKET,
            op: action,
            requestAttributes: {
              ws_event: action,
              ws_params: JSON.stringify(params),
              ws_status: params.state,
              ws_endpoint: params.state === 'open' ? params.endpoint || '' : '',
            },
          });
        }

        break;
      case 'kickOut':
        simpleLogger({ name: SENTRY_LOG.KICKOUT, op: 'WS Kicked out' });
        disposeServices();
        useAuthStore.getState().stop('errors.log-out');
        break;
      case 'message': {
        const { data, error, success } = await conversationMessageUnionScheme.safeParseAsync(params.message);
        if (!success) {
          console.error(error);
          throw error;
        }
        const message = transformMessage(data);
        const key = generateSessionKeyFromMessage(message);

        await useMessageStore.getState().add(message);
        useMessageStore
          .getState()
          .validate(
            params.type === 'chat' ? 'session' : 'group',
            params.id,
            params.message.TimeStamp,
            params.message.ID,
          );
        useConversationSessionStore.getState().addUnread(key, message, message.UserID === auth?.UserID ? 0 : 1);

        if (
          message.UserID !== auth?.UserID &&
          !notificationSettings?.IsNotDisturbAll &&
          !getSessionByKey(key)?.IsNotDisturb
        ) {
          if (!notificationSettings?.IsNotVibration && typeof navigator.vibrate === 'function') {
            navigator.vibrate(150);
          }
          if (!notificationSettings?.IsNotSound) {
            playMessageSound();
          }
        }

        if (isGroupMemberAdjustmentMessage(message)) {
          await queryClient.invalidateQueries(generateGroupInfoQueryOptions(message.GroupID));
        }

        break;
      }
      case 'read':
        useMessageStore.getState().read(params.messageId, 'session', true);
        break;
      case 'readChatSelf':
        useConversationSessionStore
          .getState()
          .clearUnreadCount(generateSessionKey('session', params.sessionId), params.messageId);
        break;
      case 'readAll':
        useMessageStore.getState().readAll('session', params.sessionId, true);
        break;
      case 'groupReadAll':
        {
          const sessionKey = generateSessionKey('group', params.groupId);
          useConversationSessionStore.getState().clearUnreadCount(sessionKey);
        }
        break;
      case 'online':
      case 'offline':
        queryClient.setQueryData(friendsQueryOptions.queryKey, (state) =>
          produce(state, (draft) => {
            const friend = draft?.[params.userId];
            if (!friend) return;

            friend.IsOnline = action === 'online';
            friend.OnlineDesc =
              action === 'online' ? t('contact.friends.online') : t('contact.friends.recently-online');
          }),
        );
        queryClient.setQueryData(generateFriendInfoQueryOptions(params.userId).queryKey, (state) =>
          produce(state, (draft) => {
            if (!draft) return;

            draft.FriendInfo.IsOnline = action === 'online';
            draft.FriendInfo.OnlineDesc =
              action === 'online' ? t('contact.friends.online') : t('contact.friends.recently-online');
          }),
        );
        break;
      case 'newSession':
        Promise.allSettled([
          queryClient.invalidateQueries(friendsQueryOptions),
          queryClient.invalidateQueries(generateFriendInfoQueryOptions(params.userId)),
          queryClient.fetchQuery(allChatSessionQueryOptions),
        ]).catch(console.warn);
        break;
      case 'leaveGroup':
      case 'disbandGroup':
        if (action === 'leaveGroup' && params.userId !== auth?.UserID) {
          // NOTE: current user is not the one got kicked out of group
          break;
        }
        useConversationSessionStore.getState().removeSession(generateSessionKey('group', params.groupId));
        useMessageStore.getState().deleteBySessionId('group', params.groupId).catch(console.error);
        queryClient.invalidateQueries(groupsQueryOptions).catch(console.warn);
        dispatch({ event: 'leave-session', data: { type: 'group', id: params.groupId } });
        break;
      case 'updateGroup':
        queryClient.setQueryData(groupsQueryOptions.queryKey, (state) =>
          produce(state, (draft) => {
            const group = draft?.[params.groupId];
            if (!group) return draft;

            group.Info = params.info ?? group.Info;
            group.GroupName = params.name ?? group.GroupName;
            group.GroupImg = params.avatarUrl ?? group.GroupImg;
          }),
        );
        break;
      case 'validateSession':
        if (params.type === 'group') {
          queryClient.invalidateQueries(generateGroupInfoQueryOptions(params.id)).catch(console.warn);
        }
        queryClient.invalidateQueries(allChatSessionQueryOptions).catch(console.warn);
        queryClient.invalidateQueries(generateSessionSettingsQueryOptions(params.type, params.id)).catch(console.warn);
        break;
      case 'validateMessage':
        if ('message' in params) {
        } else {
          useMessageStore
            .getState()
            .validate(
              params.type === 'chat' ? 'session' : 'group',
              params.sessionId,
              params.validateId,
              params.messageId,
            );
        }
        break;
      case 'purgeMessages':
        if (params.userId) {
          useMessageStore.getState().deleteByUserId(params.userId, params.type, params.sessionId).catch(console.error);
        }
        break;
      case 'joinGroup':
        Promise.allSettled([
          queryClient.invalidateQueries(groupsQueryOptions),
          queryClient.invalidateQueries(generateGroupInfoQueryOptions(params.groupId)),
          queryClient.fetchQuery(allChatSessionQueryOptions),
        ]).catch(console.warn);
        break;
      case 'groupApplication':
        queryClient.invalidateQueries(generateGroupJoinApplicationQueryOptions(params.groupId)).catch(console.warn);
        queryClient.setQueryData(generateGroupInfoQueryOptions(params.groupId).queryKey, (data) =>
          produce(data, (draft) => {
            if (draft) {
              draft.GroupJoinApplyCount = params.applicationCount;
            }
          }),
        );
        break;
      case 'deleteMessage': {
        const { type, sessionId, messageId } = params;
        const sessionKey = generateSessionKey(type, sessionId);
        useMessageStore.getState().delete(messageId, type);
        useConversationSessionStore.getState().clearUnreadCount(sessionKey, messageId);
        usePeriodicMessageStore.getState().removeBurnableMessageId(sessionKey, messageId);
        usePeriodicMessageStore.getState().addDeletedMessageId(sessionKey, messageId);
        break;
      }
      case 'muteGroupUser':
        queryClient.setQueryData(generateGroupInfoQueryOptions(params.groupId).queryKey, (state) =>
          produce(state, (draft) => {
            if (!draft) return draft;

            if (draft.Self?.UserID === params.userId) {
              draft.Self.Status = params.mute ? 0 : 1;
            }
          }),
        );
        break;
      case 'friendApplication':
        queryClient.setQueryData(friendApplicationListQueryOptions.queryKey, (state) =>
          produce(state, (draft) => {
            if (!draft) return draft;

            const index = draft.findIndex((d) => d.id === params.applicationId);
            const application = {
              id: params.applicationId,
              createTime: Date.now(),
              status: 'pending',
              ...params,
            } satisfies ApplicationDataType;
            if (index === -1) {
              draft.push(application);
            } else {
              draft[index] = application;
            }
          }),
        );
        break;
      case 'disconnect':
        simpleLogger({ name: SENTRY_LOG.KICKOUT, op: 'WS Log in failed' });
        disposeServices();
        useAuthStore.getState().stop();
        break;
      case 'updateToken':
        useAuthStore.getState().updateToken(params.token);
        break;
      default:
        break;
    }
  });

  useEffect(() => {
    if (i18n.resolvedLanguage) {
      webSocketManager.dispatch({
        action: 'localeChange',
        params: { locale: i18n.resolvedLanguage },
      });
    }
  }, [i18n.resolvedLanguage]);

  useEffect(() => {
    webSocketManager.dispatch({
      action: 'disconnect',
      params: {},
    });
    if (auth !== null) {
      webSocketManager.dispatch({
        action: 'connect',
        params: { endpoint },
      });
    }
  }, [auth, endpoint]);
}
